import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, X, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-cream-3 border border-forest/20 px-4 py-3.5 text-sm text-ink placeholder:text-sage/60 focus:border-gold focus:outline-none transition-colors";

export const EmailSdltModal = ({ open, onClose, estimate }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done

  useEffect(() => {
    if (!open) {
      setStatus("idle");
    }
  }, [open]);

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    if (open) {
      document.addEventListener("keydown", onEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!estimate) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/sdlt/email`, {
        email,
        name,
        price: estimate.price,
        total: estimate.total,
        status_text: estimate.statusText,
        bands: estimate.bands,
        additional: !!estimate.additional,
        first_time_buyer: !!estimate.firstTimeBuyer,
      });
      setStatus("done");
      toast.success("Estimate sent — check your inbox.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 503) {
        toast.error("Email service isn't live yet. Please contact us directly.");
      } else if (status === 502) {
        toast.error("That email address couldn't be verified. Try a different address.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send email. Please try again.");
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="sdlt-email-modal"
        >
          <motion.div
            className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            className="relative w-full sm:max-w-md bg-cream border border-forest/20 shadow-2xl"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 220 }}
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="sdlt-email-close"
              aria-label="Close"
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full border border-forest/20 text-forest hover:bg-forest hover:text-cream transition-colors"
            >
              <X size={16} strokeWidth={1.8} />
            </button>

            {status === "done" ? (
              <div className="p-10 sm:p-12 text-center">
                <CheckCircle2 size={44} strokeWidth={1.2} className="text-gold-dark mx-auto" />
                <h3 className="font-serif text-2xl font-medium text-forest mt-5">Estimate on its way.</h3>
                <p className="text-[13.5px] leading-[1.8] text-sage mt-3">
                  We've sent a tidy breakdown to <strong className="text-ink">{email}</strong>. Forward it to your
                  solicitor or keep it for reference.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="sdlt-email-done-close"
                  className="btn-forest mt-7"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-7 sm:p-9">
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-10 h-10 rounded-full border border-gold flex items-center justify-center">
                    <Mail size={16} strokeWidth={1.6} className="text-gold-dark" />
                  </span>
                  <div>
                    <p className="eyebrow text-gold-dark !mb-0.5">Save & Email</p>
                    <h3 className="font-serif text-xl text-forest leading-tight">Email this estimate</h3>
                  </div>
                </div>

                <p className="text-[12.5px] leading-[1.7] text-sage mb-5">
                  We'll send the band-by-band breakdown to your inbox so you can share it with your solicitor.
                </p>

                <div className="space-y-4">
                  <div>
                    <label htmlFor="sdlt-email-name" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2">
                      Your Name <span className="text-sage/60 font-normal lowercase tracking-normal">(optional)</span>
                    </label>
                    <input
                      id="sdlt-email-name"
                      data-testid="sdlt-email-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="First name"
                      className={inputCls}
                      maxLength={120}
                    />
                  </div>
                  <div>
                    <label htmlFor="sdlt-email-address" className="block text-[11px] tracking-[0.2em] uppercase font-bold text-forest mb-2">
                      Email Address
                    </label>
                    <input
                      id="sdlt-email-address"
                      data-testid="sdlt-email-address-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className={inputCls}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="sdlt-email-submit"
                  className="btn-gold w-full justify-center mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Sending..." : "Send Estimate"} <Send size={14} strokeWidth={2.2} />
                </button>

                <p className="text-[11px] leading-relaxed text-sage/80 mt-4">
                  We only use your email to send this one estimate. No marketing, no sharing.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EmailSdltModal;
